/** Normalize API status strings to translation keys / display labels. */

export type PipelineStatusCategory =
  | "running"
  | "success"
  | "failed"
  | "cancelled"
  | "skipped"
  | "other";

/** Raw API value → canonical snake_case token (no status. prefix). */
export function normalizePipelineStatusToken(status: string): string {
  return status
    .trim()
    .toLowerCase()
    .replace(/^status\./, "")
    .replace(/[\s-]+/g, "_");
}

/** Map API tokens to next-intl keys under the `status` namespace. */
export function pipelineStatusTranslationKey(status: string): string {
  const token = normalizePipelineStatusToken(status);
  const keyMap: Record<string, string> = {
    in_progress: "inProgress",
    inprogress: "inProgress",
    success: "completed",
  };
  return keyMap[token] ?? token;
}

export function getPipelineStatusCategory(
  status: string,
  options?: { runIsTerminal?: boolean }
): PipelineStatusCategory {
  const token = normalizePipelineStatusToken(status);
  // A step still sitting at "pending" once its parent run has already reached
  // a terminal state never actually got scheduled (its dependency failed
  // upstream) — it isn't in flight, so don't render it as an active spinner.
  if (token === "pending" && options?.runIsTerminal) {
    return "skipped";
  }
  if (
    ["running", "processing", "in_progress", "inprogress", "pending"].includes(
      token
    )
  ) {
    return "running";
  }
  if (["completed", "success"].includes(token)) return "success";
  if (["failed", "error"].includes(token)) return "failed";
  if (token === "cancelled") return "cancelled";
  return "other";
}

/** English fallback when translations are unavailable. */
export function formatPipelineStatusLabel(status: string): string {
  const token = normalizePipelineStatusToken(status);
  const labels: Record<string, string> = {
    completed: "Completed",
    success: "Success",
    running: "Running",
    processing: "Processing",
    in_progress: "In Progress",
    inprogress: "In Progress",
    failed: "Failed",
    error: "Error",
    pending: "Pending",
    cancelled: "Cancelled",
    unknown: "Unknown",
  };
  if (labels[token]) return labels[token];
  return token
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function isPipelineStatusRunning(status: string): boolean {
  return getPipelineStatusCategory(status) === "running";
}

type StatusTranslator = ((key: string) => string) & {
  has?: (key: string) => boolean;
};

/** Resolve a localized status label without throwing on missing keys. */
export function translatePipelineStatus(
  tStatus: StatusTranslator,
  status: string
): string {
  const candidates = [
    pipelineStatusTranslationKey(status),
    normalizePipelineStatusToken(status),
  ];

  for (const key of [...new Set(candidates)]) {
    if (tStatus.has?.(key)) {
      return tStatus(key);
    }
  }

  return formatPipelineStatusLabel(status);
}
